import { useQuery } from '@tanstack/react-query';
import YAML from 'yaml';

import axios, { parseAxiosError } from '@/portainer/services/axios';

import { queryKeys } from './query-keys';
import { GitHelmChartBaseParams } from './useGitHelmChartValues';

type ChartMetadata = {
  name?: string;
  version?: string;
  description?: string;
  apiVersion?: string;
  appVersion?: string;
  type?: string;
};

type GitHelmChartInfoParams = GitHelmChartBaseParams & {
  command: 'chart';
};

type GitFilePreviewResponse = {
  FileContent: string;
};

/**
 * Parses Chart.yaml from a Git repository and returns chart metadata
 * including version, name, description, etc.
 */
export function useGitHelmChartInfo(params: GitHelmChartInfoParams) {
  const hasValidGitParams =
    !!params.repository && !!params.chartPath && params.command === 'chart';

  return useQuery({
    queryKey: queryKeys.gitChartInfo(
      params.repository,
      params.chartPath,
      params.reference || 'refs/heads/main'
    ),
    queryFn: async () => {
      const chartYaml = await getGitHelmChartFile(params);
      // Parse Chart.yaml content
      try {
        const chartMetadata = YAML.parse(chartYaml) as ChartMetadata;

        const result = {
          name: chartMetadata?.name || '',
          version: chartMetadata?.version || '',
          description: chartMetadata?.description || '',
          apiVersion: chartMetadata?.apiVersion || '',
          appVersion: chartMetadata?.appVersion || '',
          type: chartMetadata?.type || '',
        };

        return result;
      } catch (error) {
        throw new Error('Invalid Chart.yaml format');
      }
    },
    enabled: hasValidGitParams,
    retry: false,
  });
}

/**
 * Gets Chart.yaml content from a Git repository
 */
async function getGitHelmChartFile(params: GitHelmChartInfoParams) {
  try {
    const {
      repository,
      chartPath,
      reference = 'refs/heads/main',
      username,
      password,
      authorizationType = 0,
      gitCredentialID = 0,
      stackID,
      tlsSkipVerify = false,
    } = params;

    const targetFile = `${chartPath}/Chart.yaml`;

    const payload = {
      repository,
      targetFile,
      reference,
      username,
      password,
      authorizationType,
      gitCredentialID,
      stackID,
      tlsSkipVerify,
    };

    const response = await axios.post<GitFilePreviewResponse>(
      '/gitops/repo/file/preview',
      payload
    );

    return response.data.FileContent;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to get Chart.yaml from Git repository');
  }
}
